/**
 * useContainerWidth — observe an element's width via ResizeObserver.
 *
 * Use when: a component needs to react to its parent window/container
 * width (not the viewport) for layout decisions that can't be expressed
 * purely in CSS — e.g., switching between table and card list at a
 * threshold, or hiding columns based on available pixels.
 *
 * For pure styling decisions (grids, flex wrap, font scaling) prefer
 * CSS `@container` queries — they're cheaper than React re-renders.
 *
 * @example
 *   const ref = useRef<HTMLDivElement>(null);
 *   const width = useContainerWidth(ref);
 *   return <div ref={ref}>{width < 600 ? <CardList /> : <Table />}</div>;
 */
import { useEffect, useState, type RefObject } from 'react';

export function useContainerWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    /* Initialise with the current width so the first render isn't 0. */
    setWidth(node.getBoundingClientRect().width);

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [ref]);

  return width;
}

/**
 * Window breakpoint tokens — shared scale for `@container` queries and JS
 * threshold checks. Prefer these over hand-picked numbers.
 */
export const WINDOW_BREAKPOINTS = {
  xs: 320,  // pinned panel / minimal chrome
  sm: 480,  // narrow window — column stack
  md: 720,  // medium window — 2-up grids
  lg: 1024, // comfortable window
  xl: 1280, // wide window — full feature grid
} as const;

export type WindowBreakpoint = keyof typeof WINDOW_BREAKPOINTS;
